import React from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { BsPlus } from "react-icons/bs";

const formatDateMMDDYYYY = (value) => {
  if (!value) return "Date n/a";
  const raw = `${value}`.trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
  const mdy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (mdy) return `${mdy[1]}/${mdy[2]}/${mdy[3]}`;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const mm = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const dd = `${date.getUTCDate()}`.padStart(2, "0");
  return `${mm}/${dd}/${date.getUTCFullYear()}`;
};

const ManualDrawTab = ({
  errors,
  isLoadingProperties,
  hasProjects,
  propertyList,
  selectedProperty,
  setSelectedProperty,
  clearErrors,
  propertyAccountList,
  selectedAccount,
  setSelectedAccount,
  selectedDrawDate,
  onDateChange,
  lineItems,
  handleAddLine,
  handleAmountChange,
  handleAmountBlur,
  updateLineItem,
  budgetLineOptions,
  isLoadingLineItems,
  handleRemoveLine,
  handleManualSubmit,
  manualNote = "",
  setManualNote,
  noteMaxLength = 500,
  availableFundsCap,
  availableFundsFormatted,
  manualTotalAmount,
  exceedsAvailableFunds,
  pendingAmount = 0,
  pendingAmountFormatted,
  pendingItems = [],
  pendingExceedsAvailable = false,
}) => (
  <div className="manual-draw-workspace mt-4">
    <div className="manual-section-card">
      <Form.Group className="mb-3">
      <Form.Label>Property / Project</Form.Label>
      <Select
        inputId="draw-property-select"
        classNamePrefix="react-select"
        className={errors?.selectedProperty ? "draw-select-invalid" : ""}
        placeholder={isLoadingProperties ? "Loading projects..." : hasProjects ? "Select a project" : "No projects available"}
        options={propertyList}
        value={selectedProperty}
        onChange={(value) => {
          setSelectedProperty(value);
          clearErrors("selectedProperty");
        }}
        isClearable
        isLoading={isLoadingProperties}
      />
      {errors?.selectedProperty && <div className="invalid-feedback d-block">{errors.selectedProperty.message}</div>}
      {!isLoadingProperties && !hasProjects && (
        <div className="text-muted small mt-2">There are no construction properties in your portfolio.</div>
      )}
      </Form.Group>
      {selectedProperty && pendingAmount > 0 && pendingExceedsAvailable && (
        <div className="manual-pending-banner">
          <div className="manual-pending-title">
            You already have draw requests pending for processing.
          </div>
          <div className="manual-pending-subtitle">
            The new draw amount exceeds the available funds after accounting for the pending draws. You cannot submit any additional draw requests until the pending draws have been processed.
          </div>
          {pendingItems.length > 0 && (
            <ul className="manual-pending-list">
              {pendingItems.slice(0, 5).map((p, i) => (
                <li key={`${p.drawDate}-${i}`}>
                  <span>{formatDateMMDDYYYY(p.drawDate)}</span>
                  <span>
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(p.amount || 0)}
                  </span>
                </li>
              ))}
              {pendingItems.length > 5 && (
                <li className="manual-pending-more">+ {pendingItems.length - 5} more</li>
              )}
            </ul>
          )}
        </div>
      )}
      {selectedProperty && (
        <Row className="g-3 mb-3 draw-loan-stats">
          <Col xs={12} md={4}>
            <div className="draw-loan-stat-card">
              <div className="draw-loan-stat-label">Loan Amount</div>
              <div className="draw-loan-stat-value">{selectedProperty.loanAmount || "--"}</div>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="draw-loan-stat-card">
              <div className="draw-loan-stat-label">Unpaid Principal Balance</div>
              <div className="draw-loan-stat-value">{selectedProperty.unpaidPrincipalBalance || "--"}</div>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="draw-loan-stat-card">
              <div className="draw-loan-stat-label">Available Funds</div>
              <div className="draw-loan-stat-value">{selectedProperty.availableFunds || "--"}</div>
            </div>
          </Col>
        </Row>
      )}
      <Form.Group className="mb-3">
      <Form.Label>Account</Form.Label>
      <Select
        inputId="draw-account-select"
        classNamePrefix="react-select"
        placeholder={isLoadingProperties ? "Loading account..." : "Account will auto-select"}
        options={propertyAccountList}
        value={selectedAccount}
        onChange={setSelectedAccount}
        isDisabled
        isLoading={isLoadingProperties}
      />
      </Form.Group>
      <Form.Group className="mb-0">
      <Form.Label>Actual Draw Disbursement Date</Form.Label>
      <DatePicker
        selected={selectedDrawDate}
        onChange={onDateChange}
        dateFormat="MM/dd/yyyy"
        placeholderText="MM/DD/YYYY"
        className={`form-control draw-date-input ${errors?.drawDate ? "is-invalid" : ""}`}
        disabled={!hasProjects}
        maxDate={new Date()}
      />
      {errors?.drawDate && <div className="invalid-feedback d-block">{errors.drawDate.message}</div>}
      </Form.Group>
    </div>
    <div className="manual-section-card line-items">
      <div className="d-flex align-items-center justify-content-between mb-2 manual-line-items-head">
        <Form.Label className="mb-0">Line Items</Form.Label>
        <Button variant="outline-primary" size="sm" className="manual-add-line-btn" onClick={handleAddLine} disabled={!hasProjects}>
          <BsPlus className="me-2" /> Add Line
        </Button>
      </div>
      {lineItems.map((item, index) => (
        <Row className="g-3 align-items-center mb-2" key={item.id}>
          <Col md={7}>
            <CreatableSelect
              inputId={`draw-line-item-select-${item.id}`}
              classNamePrefix="react-select"
              className={errors?.lineItems?.[index]?.budgetLine ? "draw-select-invalid" : ""}
              placeholder={isLoadingLineItems ? "Loading line items..." : "Select or type budget line"}
              options={budgetLineOptions}
              value={item.budgetLine}
              onChange={(value) => {
                updateLineItem(item.id, { budgetLine: value });
                clearErrors(`lineItems.${index}.budgetLine`);
              }}
              onCreateOption={(inputValue) => {
                const created = { label: inputValue, value: inputValue, __isNew__: true };
                updateLineItem(item.id, { budgetLine: created });
                clearErrors(`lineItems.${index}.budgetLine`);
              }}
              formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
              isClearable
              isLoading={isLoadingLineItems}
              isDisabled={!hasProjects}
            />
            {errors?.lineItems?.[index]?.budgetLine && (
              <div className="invalid-feedback d-block">{errors.lineItems[index].budgetLine.message}</div>
            )}
          </Col>
          <Col md={4}>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="text"
                inputMode="decimal"
                className="draw-amount-input"
                placeholder="0.00"
                value={item.amount}
                onChange={(e) => handleAmountChange(item.id, e.target.value, index)}
                onBlur={(e) => handleAmountBlur(item.id, e.target.value)}
                disabled={!hasProjects}
                isInvalid={Boolean(errors?.lineItems?.[index]?.amount) || (exceedsAvailableFunds && Number(item.amount) > 0)}
              />
            </InputGroup>
            {errors?.lineItems?.[index]?.amount && (
              <div className="invalid-feedback d-block">{errors.lineItems[index].amount.message}</div>
            )}
          </Col>
          <Col md={1} className="text-end">
            {lineItems.length > 1 && (
              <Button variant="light" size="sm" className="manual-remove-line-btn" onClick={() => handleRemoveLine(item.id)}>
                ×
              </Button>
            )}
          </Col>
        </Row>
      ))}
    </div>
    <div className="manual-section-card manual-note-card">
      <Form.Group className="mb-0">
        <Form.Label>
          Notes / Instructions <span className="manual-note-optional">(optional)</span>
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          className="manual-note-input"
          value={manualNote}
          maxLength={noteMaxLength}
          placeholder="Add any instructions about this draw. (optional)"
          onChange={(e) => setManualNote?.(e.target.value)}
          disabled={!hasProjects}
        />
        <div className="manual-note-counter">
          {manualNote.length}/{noteMaxLength}
        </div>
      </Form.Group>
    </div>
    {exceedsAvailableFunds && (
      <div className="manual-funds-warning">
        Total draw amount <strong>${manualTotalAmount.toFixed(2)}</strong> exceeds Available Funds
        <strong> {availableFundsFormatted || `$${availableFundsCap?.toFixed(2)}`}</strong>. Reduce the amount to submit.
      </div>
    )}
    {errors?.totalAmount && !exceedsAvailableFunds && (
      <div className="invalid-feedback d-block manual-total-error">{errors.totalAmount.message}</div>
    )}
    <div className="modal-actions manual-actions">
      <Button
        className="manual-submit-btn"
        onClick={handleManualSubmit}
        disabled={!hasProjects || exceedsAvailableFunds}
      >
        Submit Draw Request
      </Button>
    </div>
  </div>
);

export default ManualDrawTab;
